/**
 * Centralized error reporting for user-facing builder paths.
 *
 * Core modules call reportError() without importing UI code. shell.js injects
 * the toast sink at boot; toolbar.js listens for status events.
 */

import { eventBus } from './events.js';

const DEFAULT_DEDUPE_MS = 8000;
const sinks = {
  showToast: null
};
const recent = new Map();

function normalizeScope(scope) {
  const value = String(scope || '').trim();
  return value || 'Builder';
}

function errorDetail(err) {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string') return err;
  if (err == null) return 'Unknown error';
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function hashKey(value) {
  let hash = 2166136261;
  const text = String(value || '');
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function shouldSuppress(key, dedupeMs) {
  const now = Date.now();
  const prev = recent.get(key) || 0;
  if (now - prev < dedupeMs) return true;
  recent.set(key, now);
  return false;
}

export function initReportError({ showToast } = {}) {
  if (typeof showToast === 'function') sinks.showToast = showToast;
}

/**
 * @param {string} scope
 * @param {unknown} err
 * @param {{
 *   user?: 'toast'|'status'|'silent',
 *   message?: string,
 *   detail?: string,
 *   level?: 'warn'|'error',
 *   duration?: number,
 *   id?: string,
 *   action?: { label: string, onClick: Function },
 *   dedupeMs?: number
 * }} options
 */
export function reportError(scope, err, options = {}) {
  const resolvedScope = normalizeScope(scope);
  const detail = options.detail || errorDetail(err);
  const message = options.message || detail;
  const user = options.user || 'silent';
  const level = options.level || (user === 'silent' ? 'warn' : 'error');
  const key = `${resolvedScope}|${user}|${message}|${detail}|${options.id || ''}`;
  const dedupeMs = Number.isFinite(options.dedupeMs) ? options.dedupeMs : DEFAULT_DEDUPE_MS;
  const suppressed = shouldSuppress(key, dedupeMs);
  const payload = {
    scope: resolvedScope,
    message,
    detail,
    error: err,
    user,
    level,
    suppressed
  };

  if (!suppressed) {
    const logger = console[level] || console.error;
    logger.call(console, `[${resolvedScope}] ${detail}`, err);
    eventBus.emit('report-error', payload);

    if (user === 'toast') {
      eventBus.emit('report-error:toast', payload);
      if (sinks.showToast) {
        sinks.showToast(message, {
          type: 'error',
          duration: options.duration,
          id: options.id || `report-error-${hashKey(key)}`,
          action: options.action
        });
      }
    } else if (user === 'status') {
      eventBus.emit('report-error:status', payload);
    }
  } else {
    // A suppressed failure emits NOTHING on the main channel — that is the
    // point of the dedupe. But an error repeating inside the 8s window is
    // exactly the kind worth reporting, so it must not be invisible to an
    // observer. This carries no UI consequence: nothing renders from it, and
    // `payload.suppressed` is already true.
    eventBus.emit('report-error:suppressed', payload);
  }

  return payload;
}

export function _resetReportErrorForTesting() {
  recent.clear();
  sinks.showToast = null;
}
